// What a plan does that needs care (spec 6.4 Apply, 6.5 plan review, 6.13 consequences): dangerous changes found
// through the server's flags (`stateful`, `role: vault`), never by matching Pulumi type names, and the sentences
// for replace impacts.
import type {
  ChangeAction,
  ChildChange,
  Deployment,
  PlanChange,
  Run,
  RunSummary,
  SharedChange,
  VaultSecretsDeletion,
} from '../api/types';
import { formatDay } from './format';
import { strings } from '../strings';

export interface DangerousChange {
  kind: 'resource' | 'vault';
  /** The resource ID, or the vault's name. */
  id: string;
  action: 'replace' | 'delete';
  sentences: string[];
}

export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const KNOWN_IMPACTS = [
  'outage',
  'code-redeploy',
  'data-deleted',
  'messages-lost',
  'endpoint-changes',
  'blocked-by-name',
] as const;
type KnownImpact = (typeof KNOWN_IMPACTS)[number];

function impactSentence(impact: string, ids: readonly string[], region: string | null): string {
  const i = strings.impacts;
  const joined = joinNames(ids);
  const many = ids.length > 1;
  switch (impact as KnownImpact) {
    case 'outage':
      return i.outage(joined, many, region);
    case 'code-redeploy':
      return i['code-redeploy'](joined, many);
    case 'data-deleted':
      return i['data-deleted'](joined, many);
    case 'messages-lost':
      return i['messages-lost'](joined, many);
    case 'endpoint-changes':
      return i['endpoint-changes'](joined, many);
    case 'blocked-by-name':
      return i['blocked-by-name'](joined, many);
    default:
      return i.unknown(impact, joined);
  }
}

/**
 * One sentence per impact, grouping the resources that have it (known impacts in a fixed order, unknown ones by
 * name), plus "recreated" for replaced resources without impacts.
 */
export function impactSentences(
  resources: readonly { id: string; impacts: readonly string[] }[],
  region: string | null = null,
): string[] {
  const byImpact = new Map<string, string[]>();
  const plain: string[] = [];
  for (const resource of resources) {
    if (resource.impacts.length === 0) plain.push(resource.id);
    for (const impact of resource.impacts) {
      const list = byImpact.get(impact) ?? [];
      list.push(resource.id);
      byImpact.set(impact, list);
    }
  }
  const order = [
    ...KNOWN_IMPACTS.filter((impact) => byImpact.has(impact)),
    ...[...byImpact.keys()].filter((impact) => !(KNOWN_IMPACTS as readonly string[]).includes(impact)),
  ];
  const sentences = order.map((impact) => impactSentence(impact, byImpact.get(impact)!, region));
  if (plain.length) sentences.push(strings.impacts.recreated(joinNames(plain), plain.length > 1, region));
  return sentences;
}

const DANGEROUS: ReadonlySet<ChangeAction> = new Set(['replace', 'delete']);

/**
 * Dangerous changes of a plan (spec 6.5 item 4): a replace or delete of a stateful resource or of the vault
 * (shared changes with `role: vault`; the vault's resources count once).
 */
export function dangerousChanges(
  plan: { changes: readonly PlanChange[]; sharedChanges: readonly SharedChange[] } | null | undefined,
  deploymentName: string,
  vaultName?: string | null,
  deletion?: VaultSecretsDeletion | null,
): DangerousChange[] {
  const result: DangerousChange[] = [];
  // Feature spec 15 (U2.1): the deletion of a namespace vault's secrets counts as a dangerous change, listed first.
  const deleting = deletion ? vaultSecretsDeletionText(deploymentName, deletion) : null;
  if (!plan)
    return deletion ? [{ kind: 'vault', id: deletion.prefix, action: 'delete', sentences: [deleting!] }] : [];
  for (const change of plan.changes) {
    if (!change.stateful || !DANGEROUS.has(change.action)) continue;
    const action = change.action as 'replace' | 'delete';
    result.push({
      kind: 'resource',
      id: change.resourceId,
      action,
      sentences:
        action === 'delete'
          ? [strings.impacts.statefulDelete(change.resourceId)]
          : impactSentences([{ id: change.resourceId, impacts: change.replaceImpacts ?? [] }]),
    });
  }
  const vault = plan.sharedChanges.find((change) => change.role === 'vault' && DANGEROUS.has(change.action));
  if (vault) {
    const name = vaultName ?? vault.name;
    const action = vault.action as 'replace' | 'delete';
    result.unshift({
      kind: 'vault',
      id: name,
      action,
      sentences: [
        action === 'replace' ? strings.impacts.vaultReplaced(name) : strings.impacts.vaultDeleted(name),
        strings.impacts.vaultValues(deploymentName),
      ],
    });
  }
  if (deletion)
    result.unshift({ kind: 'vault', id: deletion.prefix, action: 'delete', sentences: [deleting!] });
  return result;
}

/** "The apply deletes dev's 5 secrets in AWS Secrets Manager (nebisys/1a2b3c4d/…): …" (feature spec 15 U2.1). */
export function vaultSecretsDeletionText(deploymentName: string, deletion: VaultSecretsDeletion): string {
  return deletion.retentionDays > 0 && deletion.recoverableUntil
    ? strings.vaults.planDeletion(
        deploymentName,
        deletion,
        formatDay(deletion.recoverableUntil),
        deletion.systemCount > 0,
      )
    : strings.vaults.planDeletionNow(deploymentName, deletion);
}

export type PlanBlocker = 'notCompleted' | 'newerVersion' | 'settingsChanged' | 'appliedAfter';

/**
 * Why a plan can't be applied as reviewed (spec 6.4): it must be a completed plan of the latest version, made with
 * the deployment's current settings (equal `settingsHash`; a null hash never equals), and finish after the last
 * successful apply. Empty when it is reviewable.
 */
export function planBlockers(
  plan: Pick<RunSummary | Run, 'kind' | 'status' | 'blueprintVersion' | 'settingsHash' | 'finishedAt'>,
  deployment: Pick<Deployment, 'settingsHash' | 'lastSuccessfulApply'>,
  latestVersion: number | undefined,
): PlanBlocker[] {
  const blockers: PlanBlocker[] = [];
  if (plan.kind !== 'plan' || plan.status !== 'completed') blockers.push('notCompleted');
  if (latestVersion !== undefined && plan.blueprintVersion !== latestVersion) blockers.push('newerVersion');
  if (
    plan.settingsHash === null ||
    deployment.settingsHash === null ||
    plan.settingsHash !== deployment.settingsHash
  )
    blockers.push('settingsChanged');
  const applied = deployment.lastSuccessfulApply?.completedAt;
  if (applied && (!plan.finishedAt || Date.parse(plan.finishedAt) <= Date.parse(applied)))
    blockers.push('appliedAfter');
  return blockers;
}

/** The first characters of a settings hash for display ("a3f9c2"). */
export function shortHash(hash: string | null | undefined): string {
  if (!hash) return '–';
  const value = hash.includes(':') ? hash.slice(hash.indexOf(':') + 1) : hash;
  return value.slice(0, 6);
}

/** Labels of child resources and shared changes by the last part of their Pulumi type; else the raw type. */
const TYPE_LABELS: Record<string, string> = {
  RoleAssignment: 'role assignment',
  Vault: 'vault',
  ResourceGroup: 'resource group',
  Namespace: 'namespace',
  StorageAccount: 'storage account',
  Secret: 'secret',
  // Feature spec 17: an attached deployment's spoke.
  VirtualNetwork: 'virtual network',
  NetworkSecurityGroup: 'network security group',
  RouteTable: 'route table',
  PrivateZone: 'private DNS zone',
};

export function pulumiTypeLabel(pulumiType: string): string {
  const last = pulumiType.split(':').pop() ?? pulumiType;
  return TYPE_LABELS[last] ?? pulumiType;
}

/** "1 role assignment" / "2 role assignments", grouped by label. */
export function childrenSummary(children: readonly ChildChange[]): string[] {
  const counts = new Map<string, number>();
  for (const child of children) {
    const label = pulumiTypeLabel(child.pulumiType);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts].map(([label, n]) => strings.planReview.childrenCount(n, label));
}

/** Changed property names that aren't settings (the variables show those). */
export function otherProperties(change: PlanChange): string[] {
  return change.changedProperties.filter((p) => !/(^|[.:])(settings|siteConfig|appSettings)$/i.test(p));
}

/** Display order of the changes table: dangerous ones first, then delete, replace, update, create, unchanged. */
export function reviewOrder(changes: readonly PlanChange[]): PlanChange[] {
  const rank: Record<ChangeAction, number> = { delete: 1, replace: 2, update: 3, create: 4, 'no-change': 5 };
  const danger = (c: PlanChange) => (c.stateful && DANGEROUS.has(c.action) ? 0 : rank[c.action]);
  return [...changes]
    .map((c, i) => ({ c, i }))
    .sort((a, b) => danger(a.c) - danger(b.c) || a.i - b.i)
    .map((x) => x.c);
}
